import { AnyAri } from '@atlassian/ari/any-ari';
import type { SearchCombinedUnitMetadata } from '@atlassian/search-client/config';

import { getSiteCloudId } from '../../constants/filters/site';
import type { QueryParams } from '../query-params/types';

/** Extracts the cloudId from a result ARI; returns undefined for blank/malformed/non-ARI ids. */
export const extractCloudIdFromAri = (ari: string | null | undefined): string | undefined => {
	if (!ari) {
		return undefined;
	}

	try {
		return AnyAri.parse(ari).cloudId?.trim() || undefined;
	} catch {
		return undefined;
	}
};

/**
 * Normalises a raw `?sites=` query param into site ids. The value reaches us unparsed, so it can be
 * a comma-separated string or an already-split array.
 */
const toSiteIds = (rawSelectedSites: QueryParams[string]): string[] => {
	const sites = Array.isArray(rawSelectedSites)
		? rawSelectedSites
		: typeof rawSelectedSites === 'string'
			? rawSelectedSites.split(',')
			: [];

	return sites.map((site) => site.trim()).filter(Boolean);
};

/**
 * Builds the cloudId -> siteName lookup for the sites currently in scope: the sites selected via
 * `?sites=`, plus the current site. Ids are resolved against `unitMetadata`, so unknown or stale
 * ids are dropped. A size greater than one therefore means the search spans multiple sites.
 */
export const buildSelectedSites = (
	rawSelectedSites: QueryParams[string],
	unitMetadata: readonly SearchCombinedUnitMetadata[] | undefined,
	currentCloudId: string | undefined,
): ReadonlyMap<string, string> => {
	const selectedSites = new Map<string, string>();
	const sitesInScope = new Set(toSiteIds(rawSelectedSites));

	if (currentCloudId) {
		sitesInScope.add(currentCloudId);
	}

	for (const site of unitMetadata ?? []) {
		const siteCloudId = getSiteCloudId(site.id);

		if (sitesInScope.has(siteCloudId) && !selectedSites.has(siteCloudId)) {
			selectedSites.set(siteCloudId, site.siteName);
		}
	}

	return selectedSites;
};

/** Resolves a result's owning-site name from the prebuilt cloudId -> siteName lookup. */
export const getSiteName = (
	resultId: string | null | undefined,
	selectedSites: ReadonlyMap<string, string> | undefined,
): string | undefined => {
	const cloudId = extractCloudIdFromAri(resultId);

	return cloudId ? selectedSites?.get(cloudId) : undefined;
};

/** Site attribution for a single result, spread into result interaction metadata. */
export type SearchResultSiteMetadata = {
	/** How many sites the search covered. */
	selectedSitesCount?: number;
	/** Whether the result belongs to the current site. */
	isPrimarySite?: boolean;
};

/**
 * Site attribution for a result, derived from the `selectedSites` lookup the renderers already
 * receive.
 *
 * Returns an empty object when the search is not site-scoped, and leaves `isPrimarySite` undefined
 * when either cloudId is unknown, so unattributable results are not reported as belonging to
 * another site.
 */
export const getSiteMetadata = (
	resultId: string | null | undefined,
	currentCloudId: string | undefined,
	selectedSites: ReadonlyMap<string, string> | undefined,
): SearchResultSiteMetadata => {
	// An absent or empty lookup both mean the search was not site-scoped, so there is nothing to
	// attribute. Reporting a count of zero here would add noise to every single-site search.
	if (!selectedSites?.size) {
		return {};
	}

	const resultCloudId = extractCloudIdFromAri(resultId);

	return {
		selectedSitesCount: selectedSites.size,
		isPrimarySite: currentCloudId && resultCloudId ? resultCloudId === currentCloudId : undefined,
	};
};
